import { useCallback, useEffect, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import { Close } from "@dreamhub/designsystem/icons";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DealStatus } from "@/pages/deals/types";
import { createDealClosePayload } from "@/pages/deals/helpers";
import { isEmpty, isString } from "lodash";
import { normalizeStringValue } from "@/utils/strings";
import CloseDealToast from "@/pages/deals/components/KanbanBoard/CloseDealToast";
import { toastIcon } from "@/components/ui/toast-icons";
import { IconButton } from "@/components/ui/icon-button";
import { Button } from "@/components/ui/button";
import ToastBody from "@/components/ui/toast-body";
import { editRestrictionMessageKey } from "@/services/hooks/entities/editRestrictions";

type EntityRecord = Record<string, unknown> & { id: string | number };

interface PendingWrite {
  // Only the fields this edit changes. The request body is rebuilt at dispatch
  // time from the latest answer the server has given for this write's own
  // record, so a queued edit can never carry a snapshot taken before the
  // request ahead of it on that record was answered.
  patch: Record<string, unknown>;
  dispatch: (base: EntityRecord, body: EntityRecord) => Promise<EntityRecord>;
  onSuccess?: (updated: EntityRecord) => void;
  // Reset the list once this write lands; outside onSuccess, which unmount skips.
  resetsList?: boolean;
  // Returns true when it has already told the user what went wrong, which
  // suppresses the generic error toast.
  onError?: (error: unknown) => boolean;
  // Undoes the caller's optimistic state when the write fails. Outside onError,
  // which unmount skips, because that state can outlive this hook.
  onNotSaved?: () => void;
}

// The queue's refs follow the hook instance, which belongs to the entity type
// rather than to one record: opening another deal from a deal already on
// screen swaps the item in place without remounting. So each write carries the
// record it was made against, and the record as it stood at that moment, and
// neither is read back off a ref that may since have moved to a different one.
type QueuedWrite = PendingWrite & {
  entityId: string | number;
  baseSnapshot: EntityRecord;
};

export const useFieldUpdates = (
  item: any,
  setItem: (item: any) => void,
  updateMutation: any,
  errorMessageKey: string,
  resetItems?: () => void,
  updateDealStatus?: any,
  updateDealStage?: any,
  updateLeadStatus?: any,
  onUndo?: () => void,
  onGuardrailsError?: (dealId: string, errorData: any) => void,
  defaultQueryParams?: Record<string, string>
) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Field edits PUT the whole record, so two of them overlapping is a lost
  // write: the second body is built before the first one's response lands, and
  // the server applies whichever request it finishes last. A file field makes
  // that reachable by hand — a second upload started during the ~4s save of the
  // first deletes it. So only one request is in flight at a time and the rest
  // queue in the order they were made. Every write path in this hook goes
  // through the same queue; one that dispatched around it would re-open the
  // lost write from the other side.
  const inFlightRef = useRef(false);
  const writeQueueRef = useRef<QueuedWrite[]>([]);
  // The write currently awaiting a response. Its patch is on screen but not yet
  // in the record the server has confirmed, so the projection below has to fold
  // it back in alongside the queue.
  const activeWriteRef = useRef<QueuedWrite | null>(null);
  // The record as the pending writes leave it. Null whenever nothing is in
  // flight, so an idle edit reads the item the caller holds.
  const optimisticItemRef = useRef<EntityRecord | null>(null);
  // The most recent record the server returned, which is what the next queued
  // body is built on top of.
  const serverItemRef = useRef<EntityRecord | null>(null);
  const isMountedRef = useRef(true);
  // The record the caller currently has on screen. Tracked separately from the
  // refs above, which follow the queue rather than the view: a save can still
  // be running for a record the user has already navigated away from.
  const onScreenId = (item as EntityRecord | null)?.id;
  const onScreenIdRef = useRef<string | number | undefined>(onScreenId);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    onScreenIdRef.current = onScreenId;
  }, [onScreenId]);

  const showErrorToast = (messageKey: string = errorMessageKey) =>
    toast.error(
      <ToastBody
        title={t(messageKey)}
        actions={
          <IconButton
            size="md"
            variant="iconNoBg"
            iconColor="secondary"
            onClick={() => toast.dismiss()}
          >
            <Close />
          </IconButton>
        }
      />,
      {
        icon: null
      }
    );

  // Refetching the list mid-queue would pull back a record without the edits
  // still being saved on it, so it waits until nothing is pending. Held rather
  // than dropped: the write that asked for it has already landed, and if the
  // one behind it fails there is no later success to ask again.
  const pendingResetRef = useRef(false);
  // Every reset goes through here: a throwing one must not strand the queue or reject unhandled.
  const runReset = () => {
    try {
      resetItems?.();
    } catch {
      // The write landed; a failed list refresh is not a failed save.
    }
  };
  const resetItemsWhenSettled = () => {
    if (writeQueueRef.current.length === 0) {
      // Cleared as well as satisfied: an earlier write in this same drain may
      // have set it while this one was still queued, and leaving it standing
      // refetches the list a second time when the queue empties.
      pendingResetRef.current = false;
      runReset();
      return;
    }
    pendingResetRef.current = true;
  };

  // The given record with every write still pending for it applied in order.
  const projectOnto = useCallback((base: EntityRecord): EntityRecord => {
    const pending = activeWriteRef.current
      ? [activeWriteRef.current, ...writeQueueRef.current]
      : writeQueueRef.current;
    return pending
      .filter((write) => write.entityId === base.id)
      .reduce<EntityRecord>((record, write) => ({ ...record, ...write.patch }), {
        ...base
      });
  }, []);

  const projectPendingWrites = (): EntityRecord =>
    projectOnto(serverItemRef.current as EntityRecord);

  const runNextWrite = () => {
    const next = writeQueueRef.current.shift();
    if (!next) {
      inFlightRef.current = false;
      activeWriteRef.current = null;
      optimisticItemRef.current = null;
      serverItemRef.current = null;
      // Runs after unmount too: it only touches the query cache behind the closed drawer.
      if (pendingResetRef.current) {
        pendingResetRef.current = false;
        runReset();
      }
      return;
    }

    inFlightRef.current = true;
    activeWriteRef.current = next;
    // A write left behind by a record the user has navigated away from still
    // goes to its own record: rebasing it onto whatever is on screen now would
    // send one record's field values to the other record's endpoint.
    const base =
      serverItemRef.current?.id === next.entityId
        ? (serverItemRef.current as EntityRecord)
        : next.baseSnapshot;
    const body = { ...base, ...next.patch };

    // Driven off the promise rather than the mutation's onSuccess/onError
    // callbacks: react-query skips those once the owning component has no
    // listeners left, so an unmount mid-save would strand the queue and every
    // edit already painted as applied would silently never be sent. The
    // promise settles either way; only the parts that touch the UI are held
    // back when the component is gone.
    next.dispatch(base, body).then(
      (updated) => settleWrite(next, base, updated ?? base, undefined),
      (error) => settleWrite(next, base, null, error)
    );
  };

  const settleWrite = (
    write: QueuedWrite,
    base: EntityRecord,
    updated: EntityRecord | null,
    error: unknown
  ) => {
    activeWriteRef.current = null;
    // False once the caller has moved to a different record: the response
    // describes a record that is no longer on screen, so it must not be
    // written into the refs or painted over what replaced it.
    const isCurrentRecord = onScreenIdRef.current === write.entityId;

    // On failure the server never took this write, so the record it was built
    // on is still the latest one the server told us about. What is queued
    // behind it is replayed against that record rather than dropped with it:
    // each entry carries only its own fields, so none of them depends on the
    // failed edit having landed.
    const confirmed = updated ?? base;

    // The ref holds the server's last word on one record at a time — the one
    // on screen, or the one a still-draining queue belongs to.
    if (isCurrentRecord || serverItemRef.current?.id === write.entityId) {
      serverItemRef.current = confirmed;
    }

    let projected: EntityRecord | null = null;
    if (isCurrentRecord) {
      projected = projectPendingWrites();
      optimisticItemRef.current = projected;
    } else {
      // Off screen there is no ref tracking this record, so the answer is kept
      // on the entries still queued for it. Without this they would go out on
      // the snapshot taken before this response, putting back whatever the
      // server added or normalised while answering.
      writeQueueRef.current = writeQueueRef.current.map((queued) =>
        queued.entityId === write.entityId
          ? { ...queued, baseSnapshot: confirmed }
          : queued
      );
    }

    if (updated && write.resetsList) {
      resetItemsWhenSettled();
    }

    if (!updated) {
      try {
        write.onNotSaved?.();
      } catch {
        // Same as the callbacks below: must not strand the queue.
      }
    }

    if (isMountedRef.current) {
      // These callbacks belong to the caller — a toast, a navigation, a
      // guardrails handler. One of them throwing must not leave the queue
      // stranded with inFlightRef still set, which would silently swallow
      // every edit made afterwards.
      let handledByCaller = false;
      try {
        if (projected) {
          setItem(projected);
        }
        if (updated) {
          write.onSuccess?.(updated);
        } else {
          handledByCaller = write.onError?.(error) === true;
        }
      } catch {
        // Deliberately not reported as a failed save when the write itself
        // succeeded: the edit did land, and telling the user it did not would
        // send them to re-enter a value the server already holds.
        handledByCaller = false;
      }
      if (!updated && !handledByCaller) {
        showErrorToast(editRestrictionMessageKey(error));
      }
    }

    runNextWrite();
  };

  const enqueueWrite = (write: PendingWrite) => {
    const queued: QueuedWrite = {
      ...write,
      entityId: (item as EntityRecord).id,
      baseSnapshot: { ...item } as EntityRecord
    };
    // Reseeded when a different record is being edited as well as when nothing
    // is in flight — otherwise an edit made just after navigating away from a
    // record whose save is still running is built on, and sent to, that record.
    if (
      !inFlightRef.current ||
      serverItemRef.current?.id !== queued.entityId
    ) {
      serverItemRef.current = queued.baseSnapshot;
    }
    writeQueueRef.current.push(queued);

    // Applied now whether the request goes out now or waits its turn, so the
    // field shows the edit immediately and the next one folds into it.
    const projected = projectPendingWrites();
    optimisticItemRef.current = projected;
    setItem(projected);

    if (!inFlightRef.current) {
      runNextWrite();
    }
  };

  const onUpdateDealStage = (
    newStage: number,
    successToastText?: string,
    onSuccessFn?: () => void
  ) => {
    if (!item) return;

    enqueueWrite({
      patch: { stage: newStage },
      dispatch: (base) =>
        updateDealStage.mutateAsync({
          pathParams: { dealId: base.id, newStageId: newStage },
          data: {}
        }),
      onSuccess: () => {
        if (successToastText) {
          toast.success(<ToastBody title={successToastText} />, {
            duration: 5000
          });
        }
        onSuccessFn?.();
      },
      onError: (error: unknown) => {
        const response = (
          error as { response?: { status?: number; data?: unknown } }
        )?.response;
        if (response?.status === 400 && onGuardrailsError) {
          onGuardrailsError(String(item.id), response.data);
          return true;
        }
        return false;
      }
    });
  };

  const onUpdateDealStatus = (
    newStatus: number,
    additionalFields?: { [fieldName: string]: string | number },
    successToastText?: string,
    onSuccessFn?: () => void,
    isDetailedView: boolean = false,
    hideSuccessToast: boolean = false,
    onNotSavedFn?: () => void
  ) => {
    if (!item) {
      onNotSavedFn?.();
      return;
    }

    const dealCloseData =
      additionalFields?.reason != null
        ? {
            reason: additionalFields.reason as number,
            comment: additionalFields.comment as string
          }
        : undefined;

    const contractData =
      newStatus === DealStatus.CLOSED_WON &&
      additionalFields?.contractName &&
      additionalFields?.companyId &&
      additionalFields?.productId &&
      additionalFields?.currency
        ? {
            contractName: additionalFields.contractName as string,
            companyId: additionalFields.companyId as string,
            productId: additionalFields.productId as string,
            currency: additionalFields.currency as string,
            contractSignDate:
              (additionalFields.contractSignDate as string | null | undefined) ??
              null,
            aeOwner:
              (additionalFields.aeOwner as string | null | undefined) ?? null,
            csmOwner:
              (additionalFields.csmOwner as string | null | undefined) ?? null
          }
        : undefined;

    enqueueWrite({
      patch: {
        status: newStatus,
        closedLostReason: additionalFields?.reason,
        closedLostReasonText: additionalFields?.comment || null
      },
      dispatch: (base) =>
        updateDealStatus.mutateAsync(
          createDealClosePayload(
            String(base.id),
            newStatus as DealStatus,
            dealCloseData,
            contractData
          )
        ),
      resetsList: !isDetailedView,
      onSuccess: (updatedDeal: EntityRecord) => {
        if (!hideSuccessToast) {
          if (successToastText) {
            toast.success(<ToastBody title={successToastText} />, {
              duration: 5000,
              icon: null
            });
          } else if (
            newStatus === DealStatus.CLOSED_WON &&
            updatedDeal?.contractId
          ) {
            const contractId = updatedDeal.contractId;
            toast.success(
              <ToastBody
                title={t("deals.dealView.contractCreatedToast.title")}
                subtitle={t("deals.dealView.contractCreatedToast.body")}
                actions={
                  <>
                    <Button
                      variant="secondaryNoBg"
                      onClick={() => {
                        navigate(`/contracts/${contractId}`);
                        toast.dismiss();
                      }}
                    >
                      {t("deals.dealView.contractCreatedToast.viewContract")}
                    </Button>
                    <IconButton
                      variant="iconNoBg"
                      iconColor="secondary"
                      size="md"
                      onClick={() => toast.dismiss()}
                    >
                      <Close />
                    </IconButton>
                  </>
                }
              />,
              { duration: 5000 }
            );
          } else if (
            [DealStatus.CLOSED_WON, DealStatus.CLOSED_LOST].includes(newStatus)
          ) {
            toast.success(
              <CloseDealToast
                dealId={item?.id}
                dealName={item?.name}
                status={newStatus}
                handleUndo={() => {
                  if (onUndo) {
                    onUndo();
                    toast.dismiss();
                  }
                }}
                hideViewDeal={isDetailedView}
              />,
              {
                duration: 5000,
                icon: toastIcon.info
              }
            );
          }
        }

        onSuccessFn?.();
      },
      onNotSaved: onNotSavedFn
    });
  };

  const onUpdateLeadStatus = (
    newStatus: number,
    additionalFields?: {
      [fieldName: string]: any;
      status?: number;
      successToastText?: string;
      onSuccessFn?: () => void;
    }
  ) => {
    if (!item) return;

    const successToastText = additionalFields?.successToastText;
    const onSuccessFn = additionalFields?.onSuccessFn;
    const dataFields = { ...additionalFields };
    delete dataFields.status;
    delete dataFields.successToastText;
    delete dataFields.onSuccessFn;

    enqueueWrite({
      patch: { status: newStatus, ...dataFields },
      dispatch: (base) =>
        updateLeadStatus.mutateAsync({
          pathParams: { leadId: base.id },
          data: {
            status: newStatus,
            ...dataFields
          }
        }),
      resetsList: true,
      onSuccess: () => {
        if (successToastText) {
          toast.success(<ToastBody title={successToastText} />, {
            duration: 5000,
            icon: null
          });
        }
        onSuccessFn?.();
      }
    });
  };

  const onUpdateField = useCallback(
    (
      field: string,
      newValue: string | number | boolean | string[] | number[],
      additionalFields?: { [fieldName: string]: string | number },
      successToastText?: string,
      onSuccessFn?: () => void,
      isDetailedView?: boolean,
      hideSuccessToast?: boolean,
      queryParams?: Record<string, string>,
      // Runs when this call saves nothing: any no-op edit, whatever the field,
      // or a failed deal status save. Other writes don't carry it to the queue.
      onNotSavedFn?: () => void
    ) => {
      if (!item) {
        onNotSavedFn?.();
        return;
      }

      // A queued edit has already been folded into the optimistic record but
      // not yet into the item the caller holds, so comparing against `item`
      // would re-send a value that is already on its way, or drop one that
      // differs from it only because an earlier edit is still in flight.
      // Only for the record it was built from: the ref belongs to the hook
      // instance, which outlives the record on screen, and a save left running
      // for the previous one would otherwise decide this one's edit is a no-op.
      const optimistic = optimisticItemRef.current;
      const baseItem =
        optimistic && optimistic.id === (item as EntityRecord).id
          ? optimistic
          : item;

      // Check if any field (main or additional) has actually changed
      const mainFieldUnchanged =
        normalizeStringValue(baseItem[field]) == normalizeStringValue(newValue);
      const additionalFieldsUnchanged = additionalFields
        ? Object.keys(additionalFields).every(
            (key) =>
              normalizeStringValue(baseItem[key]) ==
              normalizeStringValue(additionalFields[key])
          )
        : true;

      // == and not === on purpose, in case the value is a number but the newValue is a string
      if (mainFieldUnchanged && additionalFieldsUnchanged) {
        onNotSavedFn?.();
        return;
      }

      if (
        (typeof baseItem[field] === "string" || baseItem[field] === null) &&
        (typeof newValue === "string" || newValue === null) &&
        isEmpty(baseItem[field]) &&
        isEmpty(newValue)
      ) {
        onNotSavedFn?.();
        return;
      }

      const idField =
        "stage" in item
          ? "dealId"
          : "mobilePhone" in item
            ? "partnerContactId"
            : "firstName" in item
              ? "personId"
              : "numOfEmployees" in item
                ? "companyId"
                : "areaOfResponsibility" in item
                  ? "partnerId"
                  : "leadId";

      if (idField === "dealId") {
        if (field === "status") {
          return onUpdateDealStatus(
            newValue as number,
            additionalFields,
            successToastText,
            onSuccessFn,
            isDetailedView,
            hideSuccessToast,
            onNotSavedFn
          );
        }
        if (field === "stage") {
          return onUpdateDealStage(
            newValue as number,
            successToastText,
            onSuccessFn
          );
        }
      }

      if (idField === "leadId") {
        if (field === "status") {
          return onUpdateLeadStatus(newValue as number, additionalFields);
        }
      }

      // Handle different value types appropriately
      let processedValue: any;
      if (typeof newValue === "boolean") {
        processedValue = newValue;
      } else if (Array.isArray(newValue)) {
        processedValue = newValue;
      } else if (isString(newValue) && !newValue.trim()) {
        processedValue = null;
      } else {
        processedValue = newValue;
      }

      const mergedQueryParams = { ...defaultQueryParams, ...queryParams };
      const hasQueryParams = Object.keys(mergedQueryParams).length > 0;

      enqueueWrite({
        patch: { [field]: processedValue, ...(additionalFields || {}) },
        dispatch: (base, body) =>
          updateMutation.mutateAsync({
            pathParams: { [idField]: base.id },
            data: body,
            ...(hasQueryParams && { queryParams: mergedQueryParams })
          }),
        resetsList: true,
        onSuccess: () => {
          if (successToastText) {
            toast.success(<ToastBody title={successToastText} />, {
              duration: 5000,
              icon: null
            });
          }
          onSuccessFn?.();
        }
      });
    },
    [item, updateMutation, setItem, resetItems]
  );

  // A read of this record that lands while a write is queued or in flight was
  // answered before that write, so it carries the value the write replaces.
  const hasPendingWrites = useCallback(
    (entityId: string | number | undefined) =>
      entityId != null &&
      (activeWriteRef.current?.entityId === entityId ||
        writeQueueRef.current.some((write) => write.entityId === entityId)),
    []
  );

  // A copy of the record fetched before its pending writes were sent shows the
  // values they replace, so those edits are laid back over it.
  const withPendingWrites = useCallback(
    <T extends { id: string | number } | null | undefined>(record: T): T => {
      if (!record || !hasPendingWrites(record.id)) return record;
      // Built on the server's latest answer for this record, so an earlier save
      // that already landed is not reverted by an older fetched copy.
      const ownWrite = [activeWriteRef.current, ...writeQueueRef.current].find(
        (write) => write?.entityId === record.id
      );
      const confirmed =
        serverItemRef.current?.id === record.id
          ? serverItemRef.current
          : ownWrite?.baseSnapshot;
      return projectOnto({
        ...(record as unknown as EntityRecord),
        ...confirmed
      }) as unknown as T;
    },
    [hasPendingWrites, projectOnto]
  );

  const pendingWrites = useMemo(
    () => ({ hasPendingWrites, withPendingWrites }),
    [hasPendingWrites, withPendingWrites]
  );

  return { onUpdateField, pendingWrites };
};
